import { useEffect, useState } from 'react';
import { Highlight, useHits, useSearchBox } from 'react-instantsearch';
import { useRouter } from 'next/compat/router';
import Image from 'next/image';
import Link from 'next/link';

import Search from '@/icons/light/search.svg';
import { Button, Container } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';

import SiteLogo from '@/assets/images/search-logo.png';
import { useAlgoliaDebounce, useRouteChangeStart } from '@/hooks';
import { routes } from '@/lib';
import { sortZonasResults, zonePath } from '../utils';

import type { TypeHitZone } from '@/types';
import type { SearchTypeRoutes } from '@/types/searchTypeRoutes';
import type { MouseEvent } from 'react';
import type { TypeSearchBoxDesktopProps } from './types';

/**
 * SearchBoxDesktop
 */
export const SearchBoxDesktop = ({
  className = '',
  placeholder,
  typeFlat,
}: TypeSearchBoxDesktopProps) => {
  const { refine, clear, query } = useSearchBox({
    queryHook: useAlgoliaDebounce,
  });
  const { hits: unsortedHits } = useHits<TypeHitZone>();
  const [hits, setHits] = useState(unsortedHits);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [focused, setFocused] = useState(false);
  const close = () => {
    setFocused(false);
  };
  const handleClickOutside = (event: MouseEvent<HTMLElement>) => {
    const search = document.getElementById('algolia-search-label');
    const target = event.target as HTMLElement;

    if (
      search &&
      !search.contains(target) &&
      target.id !== 'algolia-search-label'
    ) {
      setFocused(false);
      setSearchTerm(undefined);
      clear();
    }
  };

  useEffect(() => {
    // TODO: tipar correctamente

    // @ts-ignore
    document.addEventListener('click', handleClickOutside);

    // TODO: tipar correctamente

    // @ts-ignore
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setHits(sortZonasResults(unsortedHits));
  }, [unsortedHits]);

  useRouteChangeStart(close);

  const router = useRouter();
  const generateLink = (hit: TypeHitZone) =>
    `${zonePath(hit, typeFlat)}?query=${query}`;
  const navigateToFirstHit = () => {
    // hits && hits.length > 0 && router?.push(generateLink(hits[0])); // Changed functionality to redirect to flats list without selecting first hit
    if (!hits || hits.length === 0) return;

    const flatRouteCandidate =
      routes.flats[
        process.env.NEXT_PUBLIC_MAIN_SEARCH_TYPE as SearchTypeRoutes
      ];

    const target =
      typeof flatRouteCandidate === 'string'
        ? flatRouteCandidate
        : (flatRouteCandidate?.index ?? routes.index);

    router?.push(target);
  };

  return (
    <Container wrapper className={className} id="algolia-search-content">
      <div
        id="algolia-search"
        className="rounded-custom bg-primary-500 flex flex-col p-4 md:gap-6 md:p-6"
      >
        <Image
          src={SiteLogo}
          alt="Site logo image"
          className="hidden h-full md:absolute md:top-0 md:right-0 md:bottom-0 md:block"
        />

        <h2 className="text-headline text-headline-h3 text-neutrals-0 gap-4 text-center md:text-left">
          Tenemos un hogar para ti
        </h2>

        <label
          id="algolia-search-label"
          form="search"
          className="rounded-custom md:bg-neutrals-0 relative flex flex-col items-center gap-4 py-2 md:flex-row md:justify-between md:px-4"
        >
          <div className="rounded-custom bg-neutrals-0 flex w-full items-center gap-2 px-2 py-4 md:px-0 md:py-0">
            <Search width={24} height={24} className="text-neutrals-500" />

            <form
              onSubmit={(e) => e.preventDefault()}
              onKeyDown={(event) =>
                event.key === 'Enter' && navigateToFirstHit()
              }
              noValidate={true}
              className="w-full"
              autoComplete="off"
            >
              <input
                id="algolia-search-input"
                value={searchTerm ?? ''}
                name="new-search"
                onFocus={() => {
                  document
                    .getElementById('algolia-search-content')
                    ?.scrollIntoView(true);
                  setFocused(true);
                }}
                placeholder={placeholder}
                className="placeholder:text-text-neutrals-500 w-full border-0 border-transparent p-0 hover:border-0 hover:border-transparent focus:border-0 focus:border-transparent focus:ring-0 focus:ring-offset-0 focus:outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0 active:border-0 active:border-transparent"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  refine(e.target.value);
                }}
              />
            </form>
          </div>

          <Button
            size={ButtonSize.SMALL}
            variant={ButtonVariant.SECONDARY}
            className="hidden h-[58px] w-full md:block md:h-[48px]"
            onClick={navigateToFirstHit}
          >
            Buscar
          </Button>

          {focused && (
            <div className="rounded-custom divide-neutrals-100 border-neutrals-100 bg-neutrals-0 absolute top-full right-0 left-0 z-10 divide-y border border-solid px-1 py-1 md:mt-2 md:px-2 md:py-4">
              <div className="divide-neutrals-50 flex max-h-[300px] flex-col divide-y overflow-y-auto">
                {hits.length > 0 ? (
                  hits.map((hit, index) => (
                    <Link
                      href={generateLink(hit)}
                      key={index}
                      prefetch={false}
                      className="u-body u-body--m text-neutrals-500 hover:bg-neutrals-50 p-4 capitalize"
                    >
                      <Highlight
                        attribute="name"
                        classNames={{
                          highlighted: 'bg-transparent text-neutrals-900',
                        }}
                        hit={hit}
                      />
                    </Link>
                  ))
                ) : (
                  <p className="u-body u-body--m p-2">
                    Lo sentimos, por el momento no tenemos viviendas disponibles
                    en esa ciudad.
                  </p>
                )}
              </div>
            </div>
          )}
        </label>

        <Button
          size={ButtonSize.SMALL}
          variant={ButtonVariant.SECONDARY}
          className="h-[58px] w-full md:hidden md:h-[48px]"
          onClick={navigateToFirstHit}
        >
          Buscar
        </Button>
      </div>
    </Container>
  );
};

SearchBoxDesktop.displayName = 'SearchBoxDesktop';
